/**
 * Sistema de eventos para invalidação de cache de produtos
 * 
 * Permite que diferentes partes da aplicação notifiquem mudanças
 * em produtos para invalidar/atualizar o cache automaticamente
 */

type ProductCacheEventType = 
  | 'product:created'
  | 'product:updated'
  | 'product:deleted'
  | 'product:stock-updated'
  | 'product:stock-movement'
  | 'product:group-changed'
  | 'product:featured-changed'
  | 'products:bulk-updated'
  | 'cache:invalidate-all';

interface ProductCacheEvent {
  type: ProductCacheEventType;
  productId?: number;
  data?: any;
}

type EventListener = (event: ProductCacheEvent) => void;

class ProductCacheEventEmitter {
  private listeners: Map<ProductCacheEventType, Set<EventListener>> = new Map();

  /**
   * Registra um listener para um tipo de evento
   */
  on(eventType: ProductCacheEventType, listener: EventListener): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);

    // Retorna função para remover o listener
    return () => {
      this.off(eventType, listener);
    };
  }

  /**
   * Remove um listener
   */
  off(eventType: ProductCacheEventType, listener: EventListener): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * Dispara um evento para todos os listeners
   */
  emit(eventType: ProductCacheEventType, data?: any, productId?: number): void {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      const event: ProductCacheEvent = {
        type: eventType,
        productId,
        data,
      };
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error(`[ProductCacheEvents] Erro ao executar listener:`, error);
        }
      });
    }
  }

  /**
   * Remove todos os listeners
   */
  removeAllListeners(): void {
    this.listeners.clear();
  }
}

// Instância singleton
export const productCacheEvents = new ProductCacheEventEmitter();

/**
 * Helpers para disparar eventos comuns
 */
export const productCacheHelpers = {
  /**
   * Notifica que um produto foi criado
   */
  notifyProductCreated: (productId: number, productData?: any) => {
    productCacheEvents.emit('product:created', productData, productId);
  },

  /**
   * Notifica que um produto foi atualizado
   */
  notifyProductUpdated: (productId: number, productData?: any) => {
    productCacheEvents.emit('product:updated', productData, productId);
  },

  /**
   * Notifica que um produto foi deletado
   */
  notifyProductDeleted: (productId: number) => {
    productCacheEvents.emit('product:deleted', undefined, productId);
  },

  /**
   * Notifica que o estoque de um produto foi atualizado
   */
  notifyStockUpdated: (productId: number, stockData?: any) => {
    productCacheEvents.emit('product:stock-updated', stockData, productId);
  },

  /**
   * Notifica que o grupo de um produto foi alterado
   */
  notifyGroupChanged: (productId: number, oldGroupId?: number, newGroupId?: number) => {
    productCacheEvents.emit('product:group-changed', { oldGroupId, newGroupId }, productId);
  },

  /**
   * Notifica que o status de destaque foi alterado
   */
  notifyFeaturedChanged: (productId: number, isFeatured: boolean) => {
    productCacheEvents.emit('product:featured-changed', { isFeatured }, productId);
  },

  /**
   * Notifica atualização em massa de produtos
   */
  notifyBulkUpdated: (productIds?: number[]) => {
    productCacheEvents.emit('products:bulk-updated', { productIds });
  },

  /**
   * Notifica que houve uma movimentação de estoque em um produto
   * Isso deve invalidar o cache apenas desse produto específico
   */
  notifyStockMovement: (productId: number, movementData?: any) => {
    productCacheEvents.emit('product:stock-movement', movementData, productId);
  },

  /**
   * Invalida todo o cache
   */
  invalidateAll: () => {
    productCacheEvents.emit('cache:invalidate-all');
  },
};
